import React, { useEffect, useState } from "react";
import {
  Table,
  Title,
  Paper,
  Badge,
  Stack,
  Text,
  Group,
  ScrollArea,
  Skeleton,
  Pagination,
  Button,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { getSentEmails } from "../../../services/newsletterService";
import { IEmailHistory } from "../../../lib/types/Newsletter";
import "./styles.scss";

const Emails: React.FC = () => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState<IEmailHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  useEffect(() => {
    loadEmails();
  }, [page]);

  async function loadEmails() {
    setLoading(true);
    try {
      const result = await getSentEmails(page, limit);
      if (result && result.data) {
        setEmails(result.data);
        setTotalPages(Math.ceil(result.totalRecords / result.limit));
      }
    } catch (error) {
      console.error("Failed to load emails history:", error);
    }
    setLoading(false);
  }

  const rows = emails.map((email) => (
    <Table.Tr key={email._id}>
      <Table.Td>
        <Text fw={500}>{email.subject}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{email.recipientsCount} recipients</Text>
      </Table.Td>
      <Table.Td>
        <Badge
          color={email.status === "sent" ? "teal" : "red"}
          variant="light"
          radius="sm"
        >
          {email.status === "sent" ? "Sent" : "Failed"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {new Date(email.sentAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div className="emails-container">
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2} mb="xs">
              Sent Emails History
            </Title>
            <Text c="dimmed">View all previously sent newsletter broadcasts</Text>
          </div>
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => navigate("/d/emails/new")}
            color="teal"
            radius="md"
          >
            Send New Email
          </Button>
        </Group>

        <Paper p="md" radius="md" shadow="xs" withBorder>
          {loading ? (
            <Stack gap="xs">
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
            </Stack>
          ) : emails.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Text c="dimmed" mb="md">
                No sent emails found in history
              </Text>
              <Button
                variant="light"
                color="teal"
                onClick={() => navigate("/d/emails/new")}
              >
                Create your first broadcast
              </Button>
            </div>
          ) : (
            <ScrollArea>
              <Table highlightOnHover striped verticalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Subject</Table.Th>
                    <Table.Th>Recipients</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Sent On</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
            </ScrollArea>
          )}

          {totalPages > 1 && (
            <Group justify="center" mt="xl">
              <Pagination
                value={page}
                onChange={setPage}
                total={totalPages}
                size="sm"
                radius="md"
              />
            </Group>
          )}
        </Paper>
      </Stack>
    </div>
  );
};

export default Emails;
